/**
 * Local disk uploads (per-user). Store returned `url` / `url_path` in DB.
 * Assessment: POST .../assessment with multiple `files`; other types: single `file`.
 */

import { apiClient } from '../client'

export type UserUploadType =
  | 'cv'
  | 'profile_image'
  | 'assessment'
  | 'iqama_copy'
  | 'educational_documents'
  | 'passport'
  | 'iban_copy'

export type UserUploadSingleData = {
  user_id: number
  type: string
  filename: string
  path: string
  url: string
  url_path: string
}

export type UserUploadSingleResponse = {
  statusCode: number
  message: string
  data: UserUploadSingleData
}

export type UserUploadAssessmentData = {
  user_id: number
  type: 'assessment'
  count: number
  files: Array<{
    filename: string
    path: string
    url: string
    url_path: string
  }>
}

export type UserUploadAssessmentResponse = {
  statusCode: number
  message: string
  data: UserUploadAssessmentData
}

export type UserSingleUploadType = Exclude<UserUploadType, 'assessment'>

export const userUploadRepository = {
  /**
   * Single file. Field name `file`. Not for `assessment` — use `uploadAssessment`.
   */
  async upload(type: UserSingleUploadType, file: File | Blob): Promise<UserUploadSingleData> {
    const fd = new FormData()
    fd.append('file', file)
    const endpoint = `/api/uploads/user/${encodeURIComponent(type)}`
    const res = await apiClient.post<UserUploadSingleResponse>(endpoint, fd, true)
    if (!res.data) {
      throw new Error('Upload failed: no data')
    }
    return res.data
  },

  /**
   * One or more assessment images (user-scoped). Field name `files`.
   */
  async uploadAssessment(files: (File | Blob)[]): Promise<UserUploadAssessmentData> {
    if (!files.length) {
      throw new Error('uploadAssessment requires at least one file')
    }
    const fd = new FormData()
    for (const f of files) {
      fd.append('files', f)
    }
    const res = await apiClient.post<UserUploadAssessmentResponse>('/api/uploads/user/assessment', fd, true)
    if (!res.data) {
      throw new Error('Upload failed: no data')
    }
    return res.data
  },
}
